// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Marc Planeck <office@planeck.at>
import { IInput, InputType, IOutput, OutputMode } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { IDropdownSelectOption } from "view/comp/DropdownSelect";

class IOsModel {
  public static getAllAvailableIos(
    sourceArrays: (IOutput | IInput)[][],
    selectedIoType: "analog" | "digital",
  ) {
    const allOptions: IDropdownSelectOption[][] = [];
    // trending is disabled by parameter set if minInterval and hysteresis are 0
    const hasTrending = (item: IInput | IOutput) => {
      return !(item.minInterval === 0 && item.hysteresis === 0);
    };
    sourceArrays.forEach((curSource, srcIndex) => {
      const indexes = Array.from(Array(curSource.length).keys())
        .filter(
          (curId) =>
            curSource[curId].webActive &&
            ((selectedIoType === "analog" &&
              ((curSource[curId] as IInput)?.type === InputType.ANALOG ||
                (curSource[curId] as IOutput)?.mode === OutputMode.ANALOG)) ||
              (selectedIoType === "digital" &&
                (((curSource[curId] as IOutput)?.mode ?? OutputMode.ANALOG) !== OutputMode.ANALOG ||
                  ((curSource[curId] as IInput)?.type ?? InputType.ANALOG) !==
                    InputType.ANALOG))) &&
            hasTrending(curSource[curId]),
        )
        .sort((a, b) => curSource[a].name.localeCompare(curSource[b].name));

      const items: IDropdownSelectOption[] = indexes.map((curIndex) => {
        return {
          value: (srcIndex === 0 ? "input" : "output") + String(curIndex),
          index: curIndex,
          id: curSource[curIndex].id ?? curIndex,
          source: srcIndex === 0 ? "input" : "output",
          visible: true,
          label: curSource[curIndex].name,
        } as IDropdownSelectOption;
      });
      allOptions.push(items);
    });
    return allOptions;
  }

  public static getSelectedIos(
    allIos: IDropdownSelectOption[][],
    selectedInputIds: number[] | undefined,
    selectedOutputIds: number[] | undefined,
    setInvisibleOnly: boolean,
  ) {
    // we face 2 approaches:
    // we may only extract the selected items or we may also add the invisible items
    const selectedInputs = allIos[0]
      .filter(
        (curItem) =>
          setInvisibleOnly || !isSet(selectedInputIds) || selectedInputIds.includes(curItem.id),
      )
      .map((curSelectOption) => {
        return {
          ...curSelectOption,
          visible:
            !setInvisibleOnly ||
            !isSet(selectedInputIds) ||
            selectedInputIds.includes(curSelectOption.id),
        };
      });
    const selectedOutputs = allIos[1]
      .filter(
        (curItem) =>
          setInvisibleOnly || !isSet(selectedOutputIds) || selectedOutputIds.includes(curItem.id),
      )
      .map((curSelectOption) => {
        return {
          ...curSelectOption,
          visible:
            !setInvisibleOnly ||
            !isSet(selectedOutputIds) ||
            selectedOutputIds.includes(curSelectOption.id),
        };
      });
    return [
      selectedInputs.sort((a, b) => a.label.localeCompare(b.label)),
      selectedOutputs.sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }
}

export default IOsModel;
